// SPDX-FileCopyrightText: 2025 Slant Tech LLC
// SPDX-License-Identifier: GPL-3.0-or-later

import { useState } from 'react';

function Header({ mergeRequest, config, currentUser, onSync, onLogout, theme, onToggleTheme }) {
  const [syncing, setSyncing] = useState(false);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await onSync();
    } finally {
      setSyncing(false);
    }
  };

  // Get version from config, default to 'dev' if not available
  const version = config?.version || 'dev';

  return (
    <header className="bg-st-secondary border-b-2 border-st-border px-4 py-3 overflow-hidden">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4 min-w-0 flex-1">
          {/* Logo */}
          <a href="/" className="flex items-center gap-2 flex-shrink-0 min-w-0 hover:opacity-80 transition-opacity" title="Back to Dashboard">
            <svg viewBox="0 0 32 32" className="w-8 h-8 flex-shrink-0">
              <rect x="2" y="2" width="28" height="28" fill="rgb(var(--st-bg-primary))" stroke="#FF6600" strokeWidth="1.5"/>
              <g fill="#FF6600">
                <rect x="6" y="11" width="8" height="1.5"/>
                <rect x="18" y="11" width="8" height="1.5"/>
                <rect x="6" y="19.5" width="8" height="1.5"/>
                <rect x="18" y="19.5" width="8" height="1.5"/>
                <rect x="15" y="6" width="1.5" height="8"/>
                <rect x="15" y="18" width="1.5" height="8"/>
              </g>
              <rect x="11" y="13" width="10" height="6" fill="#1A1614"/>
            </svg>
            <div className="flex flex-col">
              <span className="font-display font-semibold text-lg leading-tight">KiCad Review</span>
              <span className="text-xs text-st-muted font-mono leading-tight">{version}</span>
            </div>
          </a>

          {/* MR Info */}
          {mergeRequest && (
            <div className="flex items-center gap-3 pl-4 border-l-2 border-st-border min-w-0">
              <span className="badge badge-open flex-shrink-0">
                !{mergeRequest.iid}
              </span>
              <a
                href={mergeRequest.web_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-st-text hover:text-st-accent transition-colors truncate"
                title={mergeRequest.title}
              >
                {mergeRequest.title}
              </a>
              <div className="text-xs text-st-muted flex items-center gap-1 flex-shrink-0 hidden lg:flex">
                <span className="font-mono">{mergeRequest.source_branch}</span>
                <span>→</span>
                <span className="font-mono">{mergeRequest.target_branch}</span>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          {/* Theme toggle */}
          <button
            onClick={onToggleTheme}
            className="p-1.5 text-st-muted hover:text-st-accent transition-colors"
            title={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
          >
            {theme === 'dark' ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
            )}
          </button>

          {/* Sync button */}
          <button
            onClick={handleSync}
            disabled={syncing}
            className="btn btn-secondary"
            title="Sync comments from GitLab"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              {syncing ? (
                <div className="spinner !w-4 !h-4" />
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              )}
            </span>
            Sync
          </button>

          {/* GitLab link */}
          {mergeRequest && (
            <a
              href={mergeRequest.web_url}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M22.65 14.39L12 22.13 1.35 14.39a.84.84 0 01-.3-.94l1.22-3.78 2.44-7.51A.42.42 0 014.82 2a.43.43 0 01.58 0 .42.42 0 01.11.18l2.44 7.49h8.1l2.44-7.51A.42.42 0 0118.6 2a.43.43 0 01.58 0 .42.42 0 01.11.18l2.44 7.51L23 13.45a.84.84 0 01-.35.94z"/>
              </svg>
              View in GitLab
            </a>
          )}

          {/* User info & logout */}
          {currentUser && (
            <div className="flex items-center gap-2 pl-3 border-l-2 border-st-border">
              {currentUser.avatar_url ? (
                <img
                  src={currentUser.avatar_url}
                  alt={currentUser.username}
                  className="w-8 h-8"
                />
              ) : (
                <div className="w-8 h-8 bg-st-accent flex items-center justify-center text-white text-sm font-display font-semibold">
                  {(currentUser.username || '?')[0].toUpperCase()}
                </div>
              )}
              <div className="text-sm">
                <div className="font-medium text-st-text">{currentUser.name || currentUser.username}</div>
                <div className="text-xs text-st-muted font-mono">@{currentUser.username}</div>
              </div>
              {onLogout && (
                <button
                  onClick={onLogout}
                  className="ml-2 p-1.5 hover:bg-st-tertiary text-st-muted hover:text-st-text transition-colors"
                  title="Sign out"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

export default Header;
